import React, { useEffect, useState, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { Backpack, Clock, CheckCircle, RotateCcw, Loader, DollarSign, ExternalLink, Gem, ShieldAlert, RefreshCw } from 'lucide-react'
import useInventoryStore from '../store/useInventoryStore'
import useAppStore from '../store/useAppStore'
import { formatNumber } from '../utils/formatters'
import toast from 'react-hot-toast'
import useAudioManager from '../hooks/useAudioManager'
import ConfirmModal from '../components/ConfirmModal'
import TermsAndInfo from '../components/TermsAndInfo'

const Inventory = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAppStore()
  const { 
    items, 
    loadInventory, 
    isLoading,
    getItemsByStatus,
    getAvailableStatuses,
    getStatusDisplayName,
    saleItem,
    sendItem,
    saleAllItems
  } = useInventoryStore()
  
  // State for active tab
  const [activeTab, setActiveTab] = useState(null)
  
  // State for loading buttons
  const [loadingItems, setLoadingItems] = useState({})
  
  // State for "Sell All" button
  const [isSellingAll, setIsSellingAll] = useState(false)
  
  // State for confirm modal (массовая продажа)
  const [showConfirmModal, setShowConfirmModal] = useState(false)
  
  // State for expensive item confirm modal
  const [showExpensiveConfirmModal, setShowExpensiveConfirmModal] = useState(false)
  const [expensiveItemToSell, setExpensiveItemToSell] = useState(null)

  // State for send to Steam confirm modal
  const [showSendConfirmModal, setShowSendConfirmModal] = useState(false)
  const [itemToSend, setItemToSend] = useState(null)
  
  // Инициализация Audio Manager через глобальный audioService
  const { playSound } = useAudioManager({
    sell: 'sell'
  })
  
  // Get available statuses and set first as active
  const availableStatuses = getAvailableStatuses()
  
  // Get tab from URL parameter
  const urlTab = searchParams.get('tab')
  
  // Звуки теперь управляются через useAudioManager (Web Audio API)
  // Предзагрузка происходит автоматически в хуке
  
  useEffect(() => {
    loadInventory()
  }, [])
  
  // Reload inventory when component mounts or URL tab parameter changes
  useEffect(() => {
    if (urlTab) {
      // Перезагружаем инвентарь при изменении URL параметра
      loadInventory()
    }
  }, [urlTab])
  
  // Set active tab based on URL parameter or default priority
  useEffect(() => {
    if (availableStatuses.length === 0) {
      setActiveTab(null)
      return
    }

    // Check if URL has a specific tab parameter
    if (urlTab && availableStatuses.includes(urlTab)) {
      if (activeTab !== urlTab) {
        setActiveTab(urlTab)
      }
      return
    }

    if (activeTab && availableStatuses.includes(activeTab)) {
      return
    }

    // Priority order for tabs (если доступны), иначе берем первую реально доступную
    const tabPriority = ['available', 'sending', 'sent', 'sold']
    const firstAvailableTab = tabPriority.find(status => availableStatuses.includes(status)) || availableStatuses[0]
    setActiveTab(firstAvailableTab || null)
  }, [availableStatuses, urlTab, activeTab])

  // Все звуки теперь воспроизводятся через playSound() из useAudioManager
  // Это обеспечивает надежное воспроизведение через Web Audio API

  // Handle sell item (с проверкой цены)
  const handleSaleItem = async (itemId, itemData = null) => {
    // Если передан itemData, проверяем цену
    if (itemData && itemData.win?.cost > 100000) {
      // Дорогой скин - показываем подтверждение
      setExpensiveItemToSell({ id: itemId, ...itemData })
      setShowExpensiveConfirmModal(true)
      return
    }
    
    // Обычная продажа
    await executeSaleItem(itemId)
  }
  
  // Выполнить продажу предмета (вызывается после подтверждения или сразу для дешевых)
  const executeSaleItem = async (itemId) => {
    try {
      setLoadingItems(prev => ({ ...prev, [`${itemId}_sale`]: true }))
      setShowExpensiveConfirmModal(false) // Закрываем модалку если была открыта
      await saleItem(itemId)
      // Воспроизводим звук продажи
      playSound('sell')
      toast.success(t('inventory.itemSoldSuccess'))
      // Reload inventory to update the UI
      await loadInventory()
    } catch (error) {
      toast.error(t('inventory.itemSaleError'))
      console.error('Sale error:', error)
    } finally {
      setLoadingItems(prev => ({ ...prev, [`${itemId}_sale`]: false }))
      setExpensiveItemToSell(null)
    }
  }

  // Handle send item to Steam (с подтверждением)
  const handleSendItem = (itemId, itemData = null) => {
    // Проверяем наличие трейдлинка перед выводом
    if (!user?.tradelink) {
      toast.error(t('inventory.tradeLinkRequired'))
      // Редиректим на профиль с параметром для автооткрытия модалки трейдлинка
      navigate('/profile?openTradeLink=true')
      return
    }

    setItemToSend({ id: itemId, ...itemData })
    setShowSendConfirmModal(true)
  }

  // Выполнить отправку в Steam после подтверждения
  const executeSendItem = async (itemId) => {
    try {
      setLoadingItems(prev => ({ ...prev, [`${itemId}_send`]: true }))
      setShowSendConfirmModal(false)
      await sendItem(itemId)
      toast.success(t('inventory.itemSentSuccess'))
      // Reload inventory to update the UI
      await loadInventory()
      // Переключаемся на вкладку "Выводятся" после успешного вывода
      setActiveTab('sending')
    } catch (error) {
      toast.error(t('inventory.itemSendError'))
      console.error('Send error:', error)
    } finally {
      setLoadingItems(prev => ({ ...prev, [`${itemId}_send`]: false }))
      setItemToSend(null)
    }
  }

  // Handle sell all items (вызывается после подтверждения)
  const handleSaleAllItems = async () => {
    try {
      setIsSellingAll(true)
      setShowConfirmModal(false) // Закрываем модалку
      await saleAllItems()
      // Воспроизводим звук продажи
      playSound('sell')
      toast.success(t('inventory.sellAllSuccess'))
      // Reload inventory to update the UI
      await loadInventory()
    } catch (error) {
      toast.error(t('inventory.sellAllError'))
      console.error('Sale all error:', error)
    } finally {
      setIsSellingAll(false)
    }
  }
  
  // Открыть модальное окно подтверждения
  const handleSellAllClick = () => {
    setShowConfirmModal(true)
  }

  // Unified styling for all items - single elegant border
  const getUnifiedItemStyle = () => {
    return 'border border-white border-opacity-10 bg-surface'
  }

  const getStatusInfo = (status) => {
    switch (status) {
      case 'available':
        return { icon: CheckCircle, text: t('inventory.statusAvailable'), color: 'text-green-400' }
      case 'sent':
        return { icon: CheckCircle, text: t('inventory.statusSent'), color: 'text-green-400' }
      case 'sold':
        return { icon: CheckCircle, text: t('inventory.statusSold'), color: 'text-yellow-400' }
      case 'sending':
        return { icon: Loader, text: t('inventory.statusSending'), color: 'text-blue-400' }
      case 'returned':
        return { icon: RotateCcw, text: t('inventory.statusReturned'), color: 'text-orange-400' }
      default:
        return { icon: Clock, text: t('inventory.statusProcessing'), color: 'text-gray-400' }
    }
  }

  // Get items for active tab
  const resolvedActiveTab = activeTab && availableStatuses.includes(activeTab) ? activeTab : null
  const activeItems = resolvedActiveTab ? getItemsByStatus(resolvedActiveTab) : []
  
  // Calculate sellable items (available/returned items without only_withdraw flag)
  const sellableItems = activeItems.filter(item => 
    (resolvedActiveTab === 'available' || resolvedActiveTab === 'returned') && 
    !item.win?.only_withdraw
  )
  
  // Calculate total cost of sellable items
  const totalSellableCost = sellableItems.reduce((sum, item) => {
    return sum + (parseInt(item.win?.cost) || 0)
  }, 0)
  
  // Show "Sell All" button if there are more than 2 sellable items
  const showSellAllButton = sellableItems.length > 2

  const sendItemName = itemToSend?.win?.name || t('inventory.confirmSendDefaultItem')
  
  return (
    <div className="min-h-screen bg-dark-primary text-white p-6 lg:max-w-2xl lg:m-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-accent-primary mb-2 flex items-center gap-3 justify-center">
          <Backpack size={32} />
          {t('inventory.title')}
        </h1>
      </div>

      {/* Tabs */}
      {availableStatuses.length > 0 && (
        <div className="mb-6">
          <div className="flex flex-wrap gap-2 justify-center">
            {availableStatuses.map((status) => {
              const isActive = resolvedActiveTab === status
              const itemCount = getItemsByStatus(status).length
              
              return (
                <button
                  key={status}
                  onClick={() => setActiveTab(status)}
                  className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'bg-accent-primary text-dark-primary'
                      : 'bg-surface text-text-secondary hover:bg-surface/80'
                  }`}
                >
                  {getStatusDisplayName(status, t)}
                </button>
              )
            })}
          </div>
        </div>
      )}

      {/* Sell All Button - показываем только во вкладке "В инвентаре" если есть больше 2 предметов */}
      {showSellAllButton && resolvedActiveTab === 'available' && (
        <div className="mb-4">
          <button
            onClick={handleSellAllClick}
            disabled={isSellingAll}
            className="w-full px-4 py-3 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary rounded-lg font-bold transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
          >
            {isSellingAll ? (
              <>
                <Loader size={18} className="animate-spin" />
                {t('inventory.sellingAll')}
              </>
            ) : (
              <>
                <DollarSign size={18} />
                {t('inventory.sellAll')} <Gem size={16} className="text-dark-primary" /> {totalSellableCost.toLocaleString('ru-RU')}
              </>
            )}
          </button>
        </div>
      )}

      {/* Loading State */}
      {isLoading ? (
        <div className="flex justify-center items-center py-16">
          <Loader className="animate-spin text-accent-primary" size={48} />
        </div>
      ) : (
        /* Inventory Grid */
        activeItems.length > 0 ? (
          <div className="grid grid-cols-1 gap-4">
            {activeItems.map((item) => {
              const statusInfo = getStatusInfo(item.status || 'available')
              const StatusIcon = statusInfo.icon
              const winItem = item.win // Use win object from API
              
              return (
                <div
                  key={item.id}
                  data-item-id={item.id}
                  className={`rounded-lg ${getUnifiedItemStyle()} p-4 ${
                    item.status === 'sold' || item.status === 'sent' ? 'opacity-60' : ''
                  }`}
                >
                  <div>
                    {/* Item Info Row */}
                    <div className="flex items-center gap-4 mb-3">
                      {/* Item Image */}
                      <div className={`w-16 h-16 rounded-lg ${getUnifiedItemStyle()} flex items-center justify-center overflow-hidden`}>
                        {item.win?.img ? (
                          <img 
                            src={item.win.img} 
                            alt={item.win.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Backpack size={24} className="text-text-secondary" />
                        )}
                      </div>

                      {/* Item Info */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white truncate">
                          {item.win?.name || t('inventory.unknownItem')}
                        </h3>
                        <div className="flex items-center gap-2 mt-1">
                          <StatusIcon size={16} className={`${statusInfo.color} ${item.status === 'sending' ? 'animate-spin' : ''}`} />
                          <span className={`text-sm ${statusInfo.color}`}>
                            {statusInfo.text}
                          </span>
                        </div>
                        {item.win?.cost && (
                          <p className="text-accent-primary font-bold mt-1 flex items-center gap-1">
                            <Gem size={16} className="text-accent-primary" /> {item.win.cost}
                          </p>
                        )}
                        
                        {/* Penalty for sold items with penalty field */}
                        {item.status === 'sold' && item.win?.penalty && (
                          <div className="mt-2 p-2 bg-red-900/20 border border-red-500/30 rounded-lg">
                            <p className="text-red-400 text-sm font-medium flex items-center gap-2">
                              <ShieldAlert size={14} className="text-red-400" />
                              {t('inventory.penaltyApplied')}
                            </p>
                            <p className="text-red-400 text-xs mt-1">
                              {t('inventory.penaltyAmount', { amount: item.win.penalty })}
                            </p>
                          </div>
                        )}
                        
                        {/* Upgrader badges */}
                        {item.win?.sold_upgrader === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-900/20 border border-orange-500/30 rounded-md">
                            <span className="text-orange-400 text-xs font-medium">
                              {t('inventory.soldInUpgrader')}
                            </span>
                          </div>
                        )}
                        
                        {item.win?.win_upgrader === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-md">
                            <span className="text-text-secondary text-xs">
                              {t('inventory.wonInUpgrader')}
                            </span>
                          </div>
                        )}
                        
                        {/* Contract badges */}
                        {item.win?.sold_contract === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-900/20 border border-orange-500/30 rounded-md">
                            <span className="text-orange-400 text-xs font-medium">
                              {t('inventory.soldInContract')}
                            </span>
                          </div>
                        )}
                        
                        {item.win?.win_contract === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-md">
                            <span className="text-text-secondary text-xs">
                              {t('inventory.wonInContract')}
                            </span>
                          </div>
                        )}
                        
                        {/* Market purchase badge */}
                        {item.win?.buy_market === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-md">
                            <span className="text-text-secondary text-xs">
                              {t('inventory.boughtInMarket')}
                            </span>
                          </div>
                        )}
                        
                        {/* Case Battle badge */}
                        {item.win?.win_casebattle === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-md">
                            <span className="text-text-secondary text-xs">
                              {t('inventory.wonInBattle')}
                            </span>
                          </div>
                        )}

                        {/* Classic Battle badges */}
                        {item.win?.sold_classic === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-900/20 border border-orange-500/30 rounded-md">
                            <span className="text-orange-400 text-xs font-medium">
                              {t('inventory.soldInClassic')}
                            </span>
                          </div>
                        )}

                        {item.win?.win_classic === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-md">
                            <span className="text-text-secondary text-xs">
                              {t('inventory.wonInClassic')}
                            </span>
                          </div>
                        )}

                        {/* Double badges */}
                        {item.win?.sold_double === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-900/20 border border-orange-500/30 rounded-md">
                            <span className="text-orange-400 text-xs font-medium">
                              {t('inventory.soldInDouble')}
                            </span>
                          </div>
                        )}

                        {item.win?.win_double === 1 && (
                          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 bg-white/5 border border-white/10 rounded-md">
                            <span className="text-text-secondary text-xs">
                              {t('inventory.wonInDouble')}
                            </span>
                          </div>
                        )}
                        
                        {/* Replacement item badge */}
                        {item.status === 'returned' && item.win?.changed === 1 && (
                          <div className="mt-2 p-2 bg-blue-900/20 border border-blue-500/30 rounded-lg">
                            <p className="text-blue-400 text-sm font-medium flex items-center gap-2">
                              <RefreshCw size={14} className="text-blue-400" />
                              {t('inventory.itemReplaced')}
                            </p>
                            <p className="text-blue-300 text-xs mt-1 hidden">
                              {t('inventory.itemReplacedDescription')}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons Row - only show for available/returned items */}
                    {(resolvedActiveTab === 'available' || resolvedActiveTab === 'returned') && (
                      <div className="flex gap-2 w-full">
                        {/* Sell Button - показываем всегда, но делаем неактивной если only_withdraw === true */}
                        <button
                          onClick={() => {
                            if (item.win?.only_withdraw) {
                              toast.error(t('inventory.withdrawOnlyItem'))
                            } else {
                              handleSaleItem(item.id, item)
                            }
                          }}
                          disabled={loadingItems[`${item.id}_sale`]}
                          className={`flex-1 px-3 py-2 bg-gradient-to-r from-accent-primary to-accent-secondary text-dark-primary rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 ${item.win?.only_withdraw ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          {loadingItems[`${item.id}_sale`] ? (
                            <>
                              <Loader size={14} className="animate-spin" />
                              {t('inventory.selling')}
                            </>
                          ) : (
                            <>
                              <DollarSign size={14} />
                              {t('inventory.sell')}
                            </>
                          )}
                        </button>

                        {/* Send to Steam Button */}
                        <button
                          onClick={() => handleSendItem(item.id, item)}
                          disabled={loadingItems[`${item.id}_send`]}
                          className="flex-1 px-3 py-2 bg-surface border border-white border-opacity-20 text-white rounded-lg font-medium transition-all flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white hover:bg-opacity-10"
                        >
                          {loadingItems[`${item.id}_send`] ? (
                            <>
                              <Loader size={14} className="animate-spin" />
                              {t('inventory.sending')}
                            </>
                          ) : (
                            <>
                              <ExternalLink size={14} />
                              {t('inventory.toSteam')}
                            </>
                          )}
                        </button>
                      </div>
                    )}

                  </div>
                </div>
              )
            })}
          </div>
        ) : (
          /* Empty State for Active Tab */
          <div className="text-center py-16">
            <Backpack size={80} className="mx-auto text-text-secondary mb-6 opacity-50" />
            <h3 className="text-2xl font-semibold text-text-secondary mb-3">
              {resolvedActiveTab ? 
                t('inventory.noItemsInCategory', { category: getStatusDisplayName(resolvedActiveTab, t) }) :
                t('inventory.empty')
              }
            </h3>
            {/* <p className="text-text-secondary mb-8">
              {t('inventory.itemsWillAppear')}
            </p> */}
          </div>
        )
      )}
      
      {/* Confirm Modal для массовой продажи */}
      <ConfirmModal
        isOpen={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleSaleAllItems}
        title={t('inventory.confirmSellAllTitle')}
        message={t('inventory.confirmSellAllMessage', { 
          count: sellableItems.length,
          total: formatNumber(totalSellableCost)
        })}
        confirmText={t('inventory.confirmSellAllButton')}
        cancelText={t('common.cancel')}
        isLoading={isSellingAll}
      />
      
      {/* Confirm Modal для продажи дорогого скина */}
      <ConfirmModal
        isOpen={showExpensiveConfirmModal}
        onClose={() => {
          setShowExpensiveConfirmModal(false)
          setExpensiveItemToSell(null)
        }}
        onConfirm={() => executeSaleItem(expensiveItemToSell?.id)}
        title={t('inventory.confirmSellExpensiveTitle')}
        message={t('inventory.confirmSellExpensiveMessage', { 
          name: expensiveItemToSell?.win?.name || '',
          price: formatNumber(expensiveItemToSell?.win?.cost || 0)
        })}
        confirmText={t('inventory.confirmSellExpensiveButton')}
        cancelText={t('common.cancel')}
        isLoading={loadingItems[`${expensiveItemToSell?.id}_sale`]}
      />

      {/* Confirm Modal для вывода в Steam */}
      <ConfirmModal
        isOpen={showSendConfirmModal}
        onClose={() => {
          setShowSendConfirmModal(false)
          setItemToSend(null)
        }}
        onConfirm={() => executeSendItem(itemToSend?.id)}
        title={t('inventory.confirmSendTitle')}
        message={t('inventory.confirmSendMessage', { name: sendItemName })}
        confirmText={t('inventory.confirmSendButton')}
        cancelText={t('inventory.confirmSendCancel')}
        isLoading={loadingItems[`${itemToSend?.id}_send`]}
      />

      <TermsAndInfo />
    </div>
  )
}

export default Inventory
